import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
    Box,
    Button,
    Card,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    Stack,
    Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { useSnackbar } from 'notistack'
import PropTypes from 'prop-types'

import {
    useArchiveCuttingTechnologiesMutation,
    useGetCuttingTechnologiesQuery,
} from '@/app/services/cuttingTechnologies'
import { selectOrganisationId } from '@/app/slices/organisationSlice'
import { TbxAlertDialog } from '@/common/components'

/**
 * Modal component for selecting cutting technologies when organization needs to reduce
 * from multiple technologies to one. Provides options to either upgrade to Advanced plan
 * or select one technology to keep (archiving the others).
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is visible
 * @param {Function} props.onClose - Callback function when modal should close
 * @returns {JSX.Element} The CuttingTechSelectionModal component
 */
const CuttingTechSelectionModal = ({ onClose, open }) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { enqueueSnackbar } = useSnackbar()

    const organisationId = useSelector(selectOrganisationId)

    // RTK Query hooks
    const {
        data: cuttingTechnologies,
        error: technologiesError,
        isLoading: isLoadingTechnologies,
    } = useGetCuttingTechnologiesQuery({ organisationId }, { skip: !organisationId })

    const [archiveCuttingTechnologies, { isLoading: isArchiving }] = useArchiveCuttingTechnologiesMutation()

    // Local state
    const [selectedTechId, setSelectedTechId] = useState('')
    const [showConfirmDialog, setShowConfirmDialog] = useState(false)

    // Filter unarchived cutting technologies
    const unarchivedTechnologies = useMemo(() => {
        if (!cuttingTechnologies) return []

        return Object.values(cuttingTechnologies).filter((tech) => !tech.isDeleted)
    }, [cuttingTechnologies])

    /**
     * Handle upgrading to Advanced plan
     */
    const handleUpgradeClick = () => {
        navigate('/settings/account#subscription')
        onClose()
    }

    /**
     * Handle technology selection change
     * @param {string} value - Selected cutting technology ID
     */
    const handleTechnologyChange = (event, value) => {
        setSelectedTechId(value)
    }

    /**
     * Handle clicking the "Keep Selected Technology" button
     */
    const handleKeepTechnologyClick = () => {
        if (!selectedTechId) {
            enqueueSnackbar(t('Please select a cutting technology to keep'), { variant: 'warning' })
            return
        }
        setShowConfirmDialog(true)
    }

    /**
     * Handle confirming the archiving action
     */
    const handleConfirmArchive = async () => {
        if (!selectedTechId) return

        try {
            await archiveCuttingTechnologies({
                cuttingTechnologyId: selectedTechId,
                organisationId,
            }).unwrap()

            enqueueSnackbar(t('Cutting technologies updated successfully'), { variant: 'success' })
            setShowConfirmDialog(false)
            onClose()
        } catch (error) {
            console.error('Failed to archive cutting technologies:', error)
            enqueueSnackbar(t('Failed to update cutting technologies'), { variant: 'error' })
        }
    }

    /**
     * Handle canceling the confirmation dialog
     */
    const handleCancelConfirm = () => {
        setShowConfirmDialog(false)
    }

    /**
     * Get display text for cutting technology type
     * @param {string} type - The technology type ('Flat' or 'Rotary')
     * @returns {string} Translated type display text
     */
    const getTechTypeDisplay = (type) => {
        return type === 'Flat' ? t('Flat') : t('Rotary')
    }

    // Handle loading and error states
    if (isLoadingTechnologies) {
        return (
            <Dialog
                open={open}
                slotProps={{
                    backdrop: {
                        sx: { backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.9) },
                    },
                }}
            >
                <DialogContent>
                    <Typography>{t('Loading cutting technologies...')}</Typography>
                </DialogContent>
            </Dialog>
        )
    }

    if (technologiesError) {
        return (
            <Dialog
                open={open}
                slotProps={{
                    backdrop: {
                        sx: { backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.9) },
                    },
                }}
            >
                <DialogContent>
                    <Typography color="error">{t('Failed to load cutting technologies. Please try again.')}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        onClick={() => onClose()}
                    >
                        {t('Close')}
                    </Button>
                </DialogActions>
            </Dialog>
        )
    }

    const selectedTech = unarchivedTechnologies.find((tech) => tech.cuttingTechnologyId === selectedTechId)

    return (
        <>
            <Dialog
                aria-describedby="cutting-tech-selection-description"
                aria-labelledby="cutting-tech-selection-title"
                maxWidth="sm"
                open={open}
                slotProps={{
                    backdrop: {
                        sx: { backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.9) },
                    },
                }}
                fullWidth
            >
                <DialogTitle id="cutting-tech-selection-title">{t('Cutting technology limit reached')}</DialogTitle>

                <DialogContent>
                    <Typography
                        id="cutting-tech-selection-description"
                        variant="body1"
                    >
                        {t(
                            'Your current plan allows only one cutting technology. Please choose one of the following options to continue:'
                        )}
                    </Typography>

                    <Box sx={{ mt: 2, mb: 4 }}>
                        <Typography
                            variant="strong1"
                            gutterBottom
                        >
                            {t('Option 1: Upgrade to Advanced plan')}
                        </Typography>
                        <Typography
                            color="text.secondary"
                            sx={{ mb: 2 }}
                            variant="body2"
                        >
                            {t('Get access to unlimited cutting technologies and advanced features.')}
                        </Typography>
                        <Button
                            color="primary"
                            variant="contained"
                            onClick={handleUpgradeClick}
                        >
                            {t('Upgrade to Advanced plan')}
                        </Button>
                    </Box>

                    <Box>
                        <Typography
                            variant="strong1"
                            gutterBottom
                        >
                            {t('Option 2: Select one cutting technology')}
                        </Typography>
                        <Typography
                            color="text.secondary"
                            sx={{ mb: 2 }}
                            variant="body2"
                        >
                            {t('Choose which cutting technology to keep. All others will be archived.')}
                        </Typography>

                        <Typography
                            color="text.secondary"
                            sx={{ mb: 2 }}
                            variant="body1"
                        >
                            {t('Available cutting technologies')}
                        </Typography>

                        {unarchivedTechnologies.length > 0 ? (
                            <FormControl
                                component="fieldset"
                                fullWidth
                            >
                                <RadioGroup
                                    aria-label="cutting-technology-selection"
                                    value={selectedTechId}
                                    onChange={handleTechnologyChange}
                                >
                                    <Stack
                                        direction={'row'}
                                        flexWrap="wrap"
                                        spacing={2}
                                        useFlexGap
                                    >
                                        {unarchivedTechnologies.map((tech) => (
                                            <Card
                                                elevation={0}
                                                key={tech.cuttingTechnologyId}
                                                sx={{
                                                    width: '48%',
                                                    flex: '0 0 auto',
                                                    height: 'max-content',
                                                    border: (theme) =>
                                                        selectedTechId === tech.cuttingTechnologyId
                                                            ? `1px solid ${theme.palette.primary.main}`
                                                            : `1px solid ${theme.palette.divider}`,
                                                    borderRadius: 2,
                                                    transition: 'all 0.2s ease-in-out',
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        borderColor: (theme) =>
                                                            selectedTechId === tech.cuttingTechnologyId
                                                                ? theme.palette.primary.main
                                                                : theme.palette.action.hover,
                                                        backgroundColor: (theme) =>
                                                            selectedTechId === tech.cuttingTechnologyId
                                                                ? alpha(theme.palette.primary.main, 0.04)
                                                                : theme.palette.action.hover,
                                                    },
                                                    backgroundColor: (theme) =>
                                                        selectedTechId === tech.cuttingTechnologyId
                                                            ? alpha(theme.palette.primary.main, 0.04)
                                                            : 'transparent',
                                                }}
                                            >
                                                <FormControlLabel
                                                    control={<Radio />}
                                                    label={
                                                        <Box>
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 1,
                                                                    mb: 0.5,
                                                                }}
                                                            >
                                                                <Typography
                                                                    component="span"
                                                                    sx={{ fontWeight: 500 }}
                                                                    variant="subtitle1"
                                                                >
                                                                    {tech.name}
                                                                </Typography>
                                                            </Box>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Chip
                                                                    label={getTechTypeDisplay(tech.type)}
                                                                    size="small"
                                                                    variant="outlined"
                                                                />
                                                                {tech.isDefault ? (
                                                                    <Chip
                                                                        color="primary"
                                                                        label={t('Default')}
                                                                        size="small"
                                                                        sx={{}}
                                                                        variant="filled"
                                                                    />
                                                                ) : null}
                                                            </Box>
                                                        </Box>
                                                    }
                                                    labelPlacement="start"
                                                    sx={{
                                                        m: 0,
                                                        p: 2,
                                                        width: '100%',
                                                        '& .MuiFormControlLabel-label': {
                                                            flex: 1,
                                                        },
                                                        '&:hover': {
                                                            backgroundColor: 'transparent',
                                                        },
                                                        alignItems: 'start',
                                                    }}
                                                    value={tech.cuttingTechnologyId}
                                                />
                                            </Card>
                                        ))}
                                    </Stack>
                                </RadioGroup>
                            </FormControl>
                        ) : (
                            <Typography color="text.secondary">{t('No cutting technologies available')}</Typography>
                        )}
                    </Box>
                </DialogContent>

                <DialogActions sx={{ p: 3, gap: 2 }}>
                    <Button
                        color="secondary"
                        disabled={!selectedTechId || isArchiving}
                        variant="contained"
                        onClick={handleKeepTechnologyClick}
                    >
                        {isArchiving ? t('Processing...') : t('Keep Selected Technology')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirmation Dialog */}
            <TbxAlertDialog
                buttonVariant="contained"
                cancelButtonText={t('Cancel')}
                content={
                    selectedTech ? (
                        <Box>
                            <Typography
                                mb={2}
                                variant="body1"
                            >
                                {t('Are you sure you want to keep only the following cutting technology?')}
                            </Typography>
                            <Box
                                sx={{
                                    p: 2,
                                    border: 1,
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    backgroundColor: 'background.paper',
                                }}
                            >
                                <Typography
                                    sx={{ fontWeight: 'medium' }}
                                    variant="subtitle1"
                                >
                                    {selectedTech.name}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                    <Chip
                                        label={getTechTypeDisplay(selectedTech.type)}
                                        size="small"
                                        variant="outlined"
                                    />
                                    {selectedTech.isDefault ? (
                                        <Chip
                                            color="primary"
                                            label={t('Default')}
                                            size="small"
                                            variant="outlined"
                                        />
                                    ) : null}
                                </Box>
                            </Box>
                            <Typography
                                color="warning.main"
                                sx={{ mt: 2 }}
                            >
                                {t('All other cutting technologies will be archived.')}
                            </Typography>
                        </Box>
                    ) : null
                }
                okButtonText={t('Confirm')}
                open={showConfirmDialog}
                title={t('Confirm technology selection')}
                onCancelClose={handleCancelConfirm}
                onOkClose={handleConfirmArchive}
            />
        </>
    )
}

CuttingTechSelectionModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
}

export default CuttingTechSelectionModal
